import { DocumentIcon, SearchIcon, ShipIcon, UploadIcon } from "../../shared/Icons";
import { docTypesDropdownAtom, themeAtom, vesselDropdownInputAtom } from "../../shared/atoms";
import CircleText from "../../shared/components/CircleText";
import Dropdown from "../../shared/components/Dropdown";
import { DropdownVessel } from "../models/certificates.interfaces";
import { ValidationError } from "../pages/Certificates";
import { DocumentTypes } from "./DocumentTypes";
import { VesselTable } from "./VesselTable";
import { useAtom, useAtomValue } from "jotai";
import { ChangeEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface Props {
  vesselOptions: DropdownVessel[];
  documentTypeOptions: string[];
  onSearch: (documentTypes: string[]) => void;
}

const DottedSeparator = () => <div className="h-[2px] w-[25px] border-t-2 border-dotted border-gray-500" />;

export const CertificatesHeader = ({ vesselOptions, documentTypeOptions: docTypes, onSearch }: Props) => {
  const theme = useAtomValue(themeAtom);

  const [selectedVessel, setSelectedVessel] = useAtom(vesselDropdownInputAtom);
  const [filteredVessels, setFilteredVessels] = useState<DropdownVessel[]>(vesselOptions);
  const [selectedDocTypes, setSelectedDocTypes] = useAtom(docTypesDropdownAtom);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errors, setErrors] = useState<ValidationError>({});

  const validateFormInputs = () => {
    if (selectedVessel) {
      setErrors((prevState) => ({ ...prevState, vesselName: undefined }));
    } else {
      setErrors((prevState) => ({ ...prevState, vesselName: "Please select a vessel." }));
    }

    if (selectedDocTypes.length === 0) {
      setErrors((prev) => ({
        ...prev,
        documentTypes: "Document type is required",
      }));
    }
  };

  const handleClickSearch = () => {
    onSearch(selectedDocTypes);
    validateFormInputs();
  };

  const clearSelectedVessel = (): void => {
    setSelectedVessel(undefined);
  };

  const clearSelectedDocTypes = (): void => {
    setSelectedDocTypes([]);
  };

  useEffect(() => {
    return () => {
      clearSelectedVessel();
      clearSelectedDocTypes();
      setErrors({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilteredVessels(vesselOptions);
  }, [vesselOptions]);

  useEffect(() => {
    if (selectedDocTypes.length !== 0) {
      setErrors((prevState) => ({ ...prevState, documentTypes: undefined }));
    }
  }, [selectedDocTypes]);

  const handleVesselLiveSearch = (
    event: ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    // @ts-expect-error: this below is always a ChangeEvent with a value
    const userInput = event.currentTarget.value as string;
    const filtered = vesselOptions.filter((option) =>
      option.vesselName.toLowerCase().includes(userInput.toLowerCase()),
    );

    // We don't change the vessel selection based on partial strings.
    // User needs to select precisely which vessel in case there are dupe vessel names (imoNum & code are unique).
    // If the user does not finish the query by selecting a vessel we clear the input.
    setSelectedVessel(undefined);
    setFilteredVessels(filtered);
  };

  const handleVesselSelected = (vessel: DropdownVessel) => {
    setSelectedVessel(vessel);
    setIsDropdownOpen(false);
    setErrors((prevState) => ({ ...prevState, vesselName: undefined }));
  };

  const serializeDocTypes = (selected: string[]): string => {
    const joined = selected.join(", ");
    return joined.length > 22 ? `${joined.slice(0, 22)}...` : joined;
  };

  return (
    <div className={twMerge("flex items-center justify-center pt-4", theme.home.tailwind.panelBackground)}>
      <CircleText text="1" />
      <DottedSeparator />

      <div className="flex flex-col pb-6">
        <div className={twMerge("h-[25px] text-sm font-medium", theme.uwhcAssistant.text)}>
          {!!selectedVessel && "Vessel"}
        </div>
        <Dropdown
          icon={<ShipIcon />}
          placeholder="Select a vessel"
          value={selectedVessel?.vesselName}
          floatingComponent={
            <VesselTable
              vessels={filteredVessels}
              selectedVessel={selectedVessel}
              onSelectVessel={handleVesselSelected}
              isOpen={isDropdownOpen}
            />
          }
          onChange={handleVesselLiveSearch}
          onClick={() => setIsDropdownOpen(true)}
          highlighted={!!selectedVessel}
          disabled={vesselOptions.length === 0}
          isLoading={vesselOptions.length === 0}
          error={errors?.vesselName}
        />
      </div>

      <DottedSeparator />
      <CircleText text="2" />
      <DottedSeparator />

      <div className="flex flex-col pb-6">
        <div className={twMerge("h-[25px] text-sm font-medium", theme.uwhcAssistant.text)}>
          {!!selectedDocTypes.length && "Document type"}
        </div>
        <Dropdown
          icon={<DocumentIcon width="100%" height="100%" />}
          placeholder="Select a document type"
          floatingComponent={<DocumentTypes types={docTypes} />}
          value={serializeDocTypes(selectedDocTypes)}
          error={errors?.["documentTypes"]}
          highlighted={!!selectedDocTypes.length}
          readOnly={true}
        />
      </div>

      <DottedSeparator />

      <div className="flex flex-col justify-end">
        <button
          className={twMerge(
            "flex h-[50px] flex-row rounded px-6 py-2 pt-3 focus:outline-none",
            theme.circleText.text,
            theme.myVesselsTableRow.row.primary,
            theme.myVesselsTableRow.row.hover,
          )}
          onClick={handleClickSearch}
        >
          <SearchIcon className="h-7 w-7 pr-2" color={theme.myVesselsTableRow.tooltip.background} />
          <div className="pt-[2px]">Search</div>
        </button>
      </div>
      <div className={twMerge("flex flex-col justify-center px-6", theme.home.tailwind.textDefault)}>or</div>
      <div className="flex flex-col justify-end">
        <Link to="/documents">
          <button
            className={twMerge(
              "flex h-[50px] flex-row rounded px-6 py-2 pt-3 focus:outline-none",
              theme.circleText.text,
              theme.myVesselsTableRow.row.primary,
              theme.myVesselsTableRow.row.hover,
            )}
          >
            <UploadIcon className="h-7 w-7 pr-2" color={theme.myVesselsTableRow.tooltip.background} />
            <div className="pt-[2px]">Upload documents</div>
          </button>
        </Link>
      </div>
    </div>
  );
};
